import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { ui } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Trash2, UserPlus } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
};

const MODULES = [
  { key: "website_generator", label: "Website" },
  { key: "image_generator", label: "Images" },
  { key: "document_editor", label: "Documents" },
];

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "member", modules: ["website_generator", "image_generator"] });

  const load = () => api.get("/auth/users").then(({ data }) => setUsers(data)).catch(() => toast.error("Failed to load team")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleFormModule = (m) => setForm((f) => ({ ...f, modules: f.modules.includes(m) ? f.modules.filter((x) => x !== m) : [...f.modules, m] }));

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/users", { ...form, email: form.email.trim(), name: form.name.trim() });
      toast.success("Team member added");
      setForm({ email: "", name: "", password: "", role: "member", modules: ["website_generator", "image_generator"] });
      load();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name || u.email}? This cannot be undone.`)) return;
    try { await api.delete(`/auth/users/${u.id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const userModules = (u) => (u.modules == null ? ["website_generator", "image_generator"] : u.modules);
  const toggleUserModule = async (u, m) => {
    const cur = userModules(u);
    const next = cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m];
    setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, modules: next } : x)));
    try { await api.patch(`/auth/users/${u.id}/modules`, { modules: next }); }
    catch (err) { toast.error(apiErr(err)); load(); }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="team-page">
      <div>
        <h1 className={ui.pageHeader}>Team</h1>
        <p className={ui.muted}>Manage who can access OpusOS and which tools each member can use.</p>
      </div>

      <form onSubmit={create} className={`${ui.card} p-5`} data-testid="create-user-form">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus className="w-4 h-4 text-indigo-300" />
          <h2 className={ui.cardTitle}>Add team member</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className={ui.label}>Name</label>
            <input data-testid="user-name" required className={ui.input} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" />
          </div>
          <div>
            <label className={ui.label}>Email</label>
            <input data-testid="user-email" required type="email" className={ui.input} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@company.com" />
          </div>
          <div>
            <label className={ui.label}>Password</label>
            <input data-testid="user-password" required type="text" className={ui.input} value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Temp password" />
          </div>
          <div>
            <label className={ui.label}>Role</label>
            <select data-testid="user-role" className={ui.input} value={form.role} onChange={(e) => set("role", e.target.value)}>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>
        <div className="mt-4">
          <label className={ui.label}>Tool access {form.role === "admin" && <span className="text-slate-500 normal-case">(admins get everything)</span>}</label>
          <div className="flex gap-4">
            {MODULES.map((m) => (
              <label key={m.key} className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" data-testid={`new-module-${m.key}`} disabled={form.role === "admin"}
                  checked={form.role === "admin" || form.modules.includes(m.key)} onChange={() => toggleFormModule(m.key)} />
                {m.label}
              </label>
            ))}
          </div>
        </div>
        <button type="submit" data-testid="create-user-btn" disabled={busy} className={`${ui.btnPrimary} mt-4`}>
          {busy ? <Loader2 className="w-4 h-4 spin" /> : <UserPlus className="w-4 h-4" />} Add member
        </button>
      </form>

      <div className="border border-slate-800 rounded-xl overflow-hidden bg-slate-900/50 backdrop-blur-sm">
        <table className="w-full" data-testid="team-table">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-950/60 text-[11px] font-mono uppercase tracking-wider text-slate-400">
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Role</th>
              <th className="px-4 py-3 text-left">Tool access</th>
              <th className="px-4 py-3 text-left">Joined</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500"><Loader2 className="w-4 h-4 spin inline" /></td></tr>}
            {!loading && users.map((u) => (
              <tr key={u.id} className="border-b border-slate-800/50 hover:bg-slate-800/40 transition-colors text-sm" data-testid={`team-row-${u.email}`}>
                <td className="px-4 py-3.5 text-slate-200 font-medium">{u.name}</td>
                <td className="px-4 py-3.5 text-slate-400 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3.5">
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${
                    u.role === "admin" ? "bg-amber-500/15 text-amber-300 border-amber-500/30" : "bg-slate-800 text-slate-400 border-slate-700"
                  }`}>{u.role}</span>
                </td>
                <td className="px-4 py-3.5">
                  {u.role === "admin" ? <span className="text-xs text-slate-500">All tools</span> : (
                    <div className="flex gap-3">
                      {MODULES.map((m) => (
                        <label key={m.key} className="flex items-center gap-1.5 text-xs text-slate-400">
                          <input type="checkbox" data-testid={`module-${u.email}-${m.key}`}
                            checked={userModules(u).includes(m.key)} onChange={() => toggleUserModule(u, m.key)} />
                          {m.label}
                        </label>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3.5 text-slate-400 text-xs">{fmt(u.created_at)}</td>
                <td className="px-4 py-3.5 text-right">
                  {u.id !== user?.id ? (
                    <button data-testid={`delete-user-${u.email}`} onClick={() => remove(u)} className="p-1.5 text-slate-500 hover:text-rose-400 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  ) : <span className="text-[11px] text-slate-600 font-mono">you</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
